/**
 * Scoring tone ramp, mirrored from rgip-sales-copilot's `ScorePill`
 * (`src/features/offerings/ScorePill.tsx`) so V2 (argo model) scores read the
 * same as the copilot: emerald (strong), amber (moderate), rose (weak). Hex
 * values are the light-mode `--tone-*` tokens from that repo's `globals.css`.
 *
 * Shared by the V2 score chip (columns) and the V2 breakdown drawer so both
 * color-code composite/tier identically.
 */
export type ScoreTone = "emerald" | "amber" | "rose";

/**
 * Canonical tier letters a model can assign, best → worst. `DQ` (disqualified)
 * is a distinct bucket. Used by the Model Score filter's tier multi-select; the
 * backend matches these uppercase-canonical.
 */
export const MODEL_SCORE_TIERS = ["A", "B", "C", "D", "E", "F", "DQ"] as const;

export type ModelScoreTier = (typeof MODEL_SCORE_TIERS)[number];

export const TONE_HEX: Record<ScoreTone, string> = {
  emerald: "#059669",
  amber: "#d97706",
  rose: "#e11d48",
};

/** Composite (0–100 point) → tone band. Same thresholds as rgip's `scoreTone`. */
export function compositeTone(score: number): ScoreTone {
  return score >= 80 ? "emerald" : score >= 50 ? "amber" : "rose";
}

/**
 * Tier letter → tone band. rgip has no tier-color map (tiers are prose there),
 * so we align letters to the same 3-tone ramp: A best (emerald), B mid (amber),
 * C and below weak (rose).
 */
export function tierTone(tier: string): ScoreTone {
  const letter = tier.trim().toUpperCase().charAt(0);
  if (letter === "A") return "emerald";
  if (letter === "B") return "amber";
  return "rose";
}
